import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { useSettingsEditor, SaveBar } from "@/components/admin/useSettingsEditor";

const ICON_OPTIONS = [
  "GraduationCap", "Stethoscope", "FlaskConical", "Home", "Briefcase", "Users",
  "BookOpen", "Trophy", "Presentation", "Library", "BedDouble", "Award", "HeartPulse",
  "Sparkles", "ShieldCheck",
];

function ListEditor({ label, items, onChange, keys, iconOptions = ICON_OPTIONS, testid }) {
  const setField = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const add = () => {
    const empty = keys.reduce((acc, k) => ({ ...acc, [k.key]: k.key === "icon" ? iconOptions[0] : "" }), {});
    onChange([...items, empty]);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <Button variant="outline" size="sm" onClick={add} className="rounded-full h-9"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-3 grid grid-cols-12 gap-2 items-center">
            {keys.map((k) => (
              <div key={k.key} className={`col-span-12 sm:col-span-${k.span || 4}`}>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k.label}</Label>
                {k.key === "icon" ? (
                  <select value={item.icon || iconOptions[0]} onChange={(e) => setField(i, "icon", e.target.value)}
                    className="mt-1 w-full h-10 rounded-lg border border-slate-200 text-sm px-2 bg-white">
                    {iconOptions.map((op) => (<option key={op} value={op}>{op}</option>))}
                  </select>
                ) : k.type === "textarea" ? (
                  <Textarea value={item[k.key] || ""} onChange={(e) => setField(i, k.key, e.target.value)} className="mt-1 rounded-lg border-slate-200 min-h-[70px]" />
                ) : (
                  <Input value={item[k.key] || ""} onChange={(e) => setField(i, k.key, e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                )}
              </div>
            ))}
            <div className="col-span-12 sm:col-span-1 flex justify-end">
              <button className="text-rose-500 p-2" onClick={() => remove(i)} aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StringListEditor({ label, items, onChange }) {
  const set = (i, v) => { const next = [...items]; next[i] = v; onChange(next); };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <Button variant="outline" size="sm" onClick={() => onChange([...items, ""])} className="rounded-full h-9"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
      </div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input value={v} onChange={(e) => set(i, e.target.value)} className="h-10 rounded-lg border-slate-200" />
            <button className="text-rose-500 p-2" onClick={() => onChange(items.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminContent() {
  const { draft, patchPath, save, reset, saving } = useSettingsEditor();
  if (!draft) return null;

  const welcome = draft.welcome || {};
  const stats = draft.stats || [];
  const why = draft.whyChoose || {};
  const campus = draft.campusHighlights || {};
  const affiliations = draft.affiliations || [];

  return (
    <div data-testid="cms-content-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Home Page</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Homepage Content</h1>
        <p className="text-slate-500 mt-2">Welcome, stats, why-choose, campus highlights and affiliations.</p>
      </div>

      <div className="space-y-6">
        {/* Welcome */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Welcome Section</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-slate-700 text-sm">Eyebrow</Label>
              <Input value={welcome.eyebrow || ""} onChange={(e) => patchPath("welcome.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={welcome.heading || ""} onChange={(e) => patchPath("welcome.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-700 text-sm">Paragraph 1</Label>
              <Textarea value={welcome.paragraph1 || ""} onChange={(e) => patchPath("welcome.paragraph1", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-700 text-sm">Paragraph 2</Label>
              <Textarea value={welcome.paragraph2 || ""} onChange={(e) => patchPath("welcome.paragraph2", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8">
          <ListEditor
            label="Stats Strip"
            items={stats}
            onChange={(v) => patchPath("stats", v)}
            keys={[
              { key: "value", label: "Value (e.g. 2000+)", span: 4 },
              { key: "label", label: "Label", span: 7 },
            ]}
          />
        </div>

        {/* Why Choose */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Why Choose Us</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Eyebrow</Label>
              <Input value={why.eyebrow || ""} onChange={(e) => patchPath("whyChoose.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={why.heading || ""} onChange={(e) => patchPath("whyChoose.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro</Label>
              <Textarea value={why.intro || ""} onChange={(e) => patchPath("whyChoose.intro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" />
            </div>
          </div>
          <ListEditor
            label="Features"
            items={why.items || []}
            onChange={(v) => patchPath("whyChoose.items", v)}
            keys={[
              { key: "icon", label: "Icon", span: 3 },
              { key: "title", label: "Title", span: 8 },
            ]}
          />
        </div>

        {/* Campus */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Campus Highlights</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Eyebrow</Label>
              <Input value={campus.eyebrow || ""} onChange={(e) => patchPath("campusHighlights.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={campus.heading || ""} onChange={(e) => patchPath("campusHighlights.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro</Label>
              <Textarea value={campus.intro || ""} onChange={(e) => patchPath("campusHighlights.intro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" />
            </div>
          </div>
          <ListEditor
            label="Highlight Cards"
            items={campus.items || []}
            onChange={(v) => patchPath("campusHighlights.items", v)}
            keys={[
              { key: "icon", label: "Icon", span: 3 },
              { key: "title", label: "Title", span: 4 },
              { key: "desc", label: "Description", span: 4, type: "textarea" },
            ]}
          />
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8">
          <StringListEditor label="Affiliations (Marquee)" items={affiliations} onChange={(v) => patchPath("affiliations", v)} />
        </div>
      </div>

      <SaveBar onSave={save} onReset={reset} saving={saving} />
    </div>
  );
}
